import {
  ContentCopyIcon,
  DeleteIcon,
  FolderIcon,
  InsertDriveFileIcon,
  LoadingButton,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  Typography,
} from "@firecms/ui";

import { formatFileSize, formatUpdatedAt } from "../../WebDownloadsManager.utils";
import { type ExplorerEntryListProps } from "../../WebDownloadsManager.types";

const EntryListIcon: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex size-4 shrink-0 items-center justify-center [&>span]:flex [&>span]:h-full [&>span]:w-full [&>span]:items-center [&>span]:justify-center [&>span]:leading-none">
    {children}
  </span>
);

const ExplorerEntryList: React.FC<ExplorerEntryListProps> = ({
  folders,
  files,
  deletingRelativePath,
  actionsDisabled,
  onOpenFolder,
  onCopyLink,
  onDeleteFile,
  onDeleteFolder,
}) => {
  if (folders.length === 0 && files.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-surface-300 px-6 py-10 text-center dark:border-surface-600">
        <FolderIcon size="smallest" className="mx-auto mb-2 text-surface-400" />
        <Typography className="font-medium">Dieser Ordner ist leer</Typography>
        <Typography variant="caption" color="secondary" className="mt-1">
          Lade Dateien hoch oder erstelle einen Unterordner.
        </Typography>
      </div>
    );
  }

  const handleDeleteFile = async (relativePath: string, fileName: string): Promise<void> => {
    if (!window.confirm(`Sind Sie sicher, dass Sie „${fileName}" löschen möchten?`)) {
      return;
    }

    await onDeleteFile(relativePath);
  };

  const handleDeleteFolder = async (relativePath: string, folderName: string): Promise<void> => {
    if (
      !window.confirm(
        `Sind Sie sicher, dass Sie den Ordner „${folderName}" und alle enthaltenen Dateien löschen möchten?`,
      )
    ) {
      return;
    }

    await onDeleteFolder(relativePath, folderName);
  };

  return (
    <div className="overflow-x-auto rounded-md border border-surface-200 dark:border-surface-700">
      <Table className="min-w-full">
        <TableHeader>
          <TableCell header>Name</TableCell>
          <TableCell header>Größe</TableCell>
          <TableCell header>Geändert</TableCell>
          <TableCell header>Aktionen</TableCell>
        </TableHeader>
        <TableBody>
          {folders.map((folder) => {
            const isDeleting = deletingRelativePath === folder.relativePath;

            return (
              <TableRow key={folder.relativePath}>
                <TableCell>
                  <button
                    type="button"
                    className="inline-flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 font-medium text-primary hover:bg-surface-accent-100 dark:hover:bg-surface-accent-800"
                    onClick={() => {
                      onOpenFolder(folder.relativePath);
                    }}
                  >
                    <EntryListIcon>
                      <FolderIcon size="smallest" className="text-primary" />
                    </EntryListIcon>
                    <span>{folder.name}</span>
                  </button>
                </TableCell>
                <TableCell className="text-surface-500">—</TableCell>
                <TableCell className="text-surface-500">—</TableCell>
                <TableCell>
                  <LoadingButton
                    color="error"
                    variant="outlined"
                    size="small"
                    loading={isDeleting}
                    disabled={actionsDisabled || (deletingRelativePath !== null && !isDeleting)}
                    startIcon={<DeleteIcon size="small" />}
                    onClick={() => {
                      void handleDeleteFolder(folder.relativePath, folder.name);
                    }}
                  >
                    Löschen
                  </LoadingButton>
                </TableCell>
              </TableRow>
            );
          })}
          {files.map((file) => {
            const isDeleting = deletingRelativePath === file.relativePath;

            return (
              <TableRow key={file.relativePath}>
                <TableCell>
                  <div className="inline-flex items-center gap-2">
                    <EntryListIcon>
                      <InsertDriveFileIcon size="smallest" className="text-surface-500" />
                    </EntryListIcon>
                    <span>{file.name}</span>
                  </div>
                </TableCell>
                <TableCell>{formatFileSize(file.sizeBytes)}</TableCell>
                <TableCell>{formatUpdatedAt(file.updatedAt)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-2">
                    <LoadingButton
                      variant="outlined"
                      color="primary"
                      size="small"
                      disabled={actionsDisabled || deletingRelativePath !== null}
                      startIcon={<ContentCopyIcon size="small" />}
                      onClick={() => {
                        void onCopyLink(file.relativePath);
                      }}
                    >
                      Link kopieren
                    </LoadingButton>
                    <LoadingButton
                      color="error"
                      variant="outlined"
                      size="small"
                      loading={isDeleting}
                      disabled={actionsDisabled || (deletingRelativePath !== null && !isDeleting)}
                      startIcon={<DeleteIcon size="small" />}
                      onClick={() => {
                        void handleDeleteFile(file.relativePath, file.name);
                      }}
                    >
                      Löschen
                    </LoadingButton>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default ExplorerEntryList;
